import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import axios from 'axios';

import API_BASE_URL from '../../config';
import { VariantEditor } from './CreateDish';
import { useLang } from '../../i18n/LanguageContext';

export default function EditDish({ showNotification }) {
  const { t } = useLang();
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [dish, setDish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [categories, setCategories] = useState([]); // [{name, nameZh}]
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryZh, setNewCategoryZh] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);

  const [variants, setVariants] = useState([]);
  const [useVariants, setUseVariants] = useState(false);

  const selectedCategory = watch('category');
  const newImageFile = watch('dishImage');

  useEffect(() => {
    if (newImageFile && newImageFile[0]) {
      const reader = new FileReader();
      reader.onload = e => setNewImagePreview(e.target.result);
      reader.readAsDataURL(newImageFile[0]);
    } else {
      setNewImagePreview(null);
    }
  }, [newImageFile]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/dishes/categories`)
      .then(res => { if (res.data.success) setCategories(res.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchDish = async () => {
      try {
        const token = localStorage.getItem('ownerToken');
        const res = await axios.get(`${API_BASE_URL}/dishes/dish-by-id/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          const d = res.data.data;
          setDish(d);
          reset({
            name: d.name,
            nameZh: d.nameZh || '',
            dishNumber: d.dishNumber || '',
            price: d.price,
            discountPrice: d.discountPrice || '',
            category: d.category,
            categoryZh: d.categoryZh || '',
            description: d.description,
            isPopular: d.isPopular,
          });
          // Load existing variants if any
          if (Array.isArray(d.variants) && d.variants.length > 0) {
            setVariants(d.variants.map(v => ({
              label: v.label || '',
              labelZh: v.labelZh || '',
              price: v.price != null ? String(v.price) : ''
            })));
            setUseVariants(true);
          }
        } else {
          showNotification('Failed to load dish', 'error');
          navigate('/dishes');
        }
      } catch {
        showNotification('Failed to load dish details.', 'error');
        navigate('/dishes');
      } finally {
        setLoading(false);
      }
    };
    fetchDish();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-fill Chinese category when picking an existing English category
  useEffect(() => {
    if (showNewCat || !selectedCategory) return;
    const found = categories.find(c => c.name === selectedCategory);
    if (found && found.nameZh) setValue('categoryZh', found.nameZh);
  }, [selectedCategory, categories, showNewCat, setValue]);

  const addVariant = () => setVariants(prev => [...prev, { label: '', labelZh: '', price: '' }]);
  const updateVariant = (idx, key, val) => {
    setVariants(prev => prev.map((v, i) => i === idx ? { ...v, [key]: val } : v));
  };
  const removeVariant = (idx) => setVariants(prev => prev.filter((_, i) => i !== idx));
  const toggleUseVariants = () => {
    setUseVariants(prev => {
      const next = !prev;
      if (next && variants.length === 0) {
        setVariants([{ label: '', labelZh: '', price: '' }, { label: '', labelZh: '', price: '' }]);
      }
      return next;
    });
  };

  const onSubmit = async (formData) => {
    if (useVariants) {
      const cleanVariants = variants.filter(v => v.label.trim() && v.price);
      if (cleanVariants.length < 2) {
        showNotification('Please add at least 2 variants with labels and prices, or disable variants.', 'error');
        return;
      }
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('ownerToken');
      const data = new FormData();
      if (formData.dishImage?.[0]) data.append('dishImage', formData.dishImage[0]);
      data.append('name', formData.name.trim());
      if (formData.nameZh && formData.nameZh.trim()) data.append('nameZh', formData.nameZh.trim());
      if (formData.dishNumber && formData.dishNumber.trim()) data.append('dishNumber', formData.dishNumber.trim());
      data.append('price', useVariants ? '0.01' : parseFloat(formData.price).toFixed(2));

      const cat = showNewCat ? newCategory.trim() : formData.category;
      const catZh = showNewCat ? newCategoryZh.trim() : (formData.categoryZh || '').trim();
      if (!cat) { showNotification('Category is required', 'error'); setSubmitLoading(false); return; }
      data.append('category', cat);
      if (catZh) data.append('categoryZh', catZh);

      data.append('description', formData.description.trim());
      data.append('isPopular', formData.isPopular || false);
      if (formData.discountPrice && !useVariants) data.append('discountPrice', parseFloat(formData.discountPrice).toFixed(2));

      if (useVariants) {
        const cleanVariants = variants
          .filter(v => v.label.trim() && v.price)
          .map(v => ({ label: v.label.trim(), labelZh: v.labelZh.trim() || null, price: parseFloat(v.price) }));
        data.append('variants', JSON.stringify(cleanVariants));
      } else {
        // Explicitly clear variants when disabled
        data.append('variants', '[]');
      }

      const res = await axios.put(`${API_BASE_URL}/dishes/update-dish/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        showNotification('Dish updated successfully!', 'success');
        setTimeout(() => navigate('/dishes'), 1200);
      } else {
        showNotification(`Failed: ${res.data.message}`, 'error');
      }
    } catch {
      showNotification('Failed to update dish. Please try again.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border" role="status" style={{ color: '#a05a2c' }}></div>
        <p className="loading-text">Loading dish details...</p>
      </div>
    );
  }

  if (!dish) return null;

  return (
    <motion.div
      className="dish-form-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-pencil-square me-2"></i>{t('dishes.editTitle')}</h2>
          <p className="page-subtitle">{dish.name}{dish.nameZh ? ` / ${dish.nameZh}` : ''}</p>
        </div>
        <button onClick={() => navigate('/dishes')} className="btn-secondary-action">
          <i className="bi bi-arrow-left me-1"></i>{t('common.back')}
        </button>
      </div>

      <div className="dish-form-card">
        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <div className="form-grid-2">
            {/* Left column */}
            <div>
              {/* Current Image */}
              <div className="form-group">
                <label className="form-label">{t('dishes.currentImage')}</label>
                <div className="current-image-box">
                  {dish.image ? (
                    <img src={dish.image} alt={dish.name} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div className="no-image-placeholder" style={{ display: dish.image ? 'none' : 'flex' }}>
                    <i className="bi bi-image"></i>
                    <span>{t('dishes.noImage')}</span>
                  </div>
                </div>
              </div>

              {/* New Image */}
              <div className="form-group">
                <label className="form-label">{dish.image ? t('dishes.replaceImage') : t('dishes.addImage')}</label>
                <label className="image-drop-zone small">
                  {newImagePreview ? (
                    <img src={newImagePreview} alt="New" className="image-drop-preview" />
                  ) : (
                    <div className="image-drop-placeholder">
                      <i className="bi bi-cloud-upload"></i>
                      <span>{t('dishes.uploadImage')}</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpg,image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    {...register('dishImage', {
                      validate: files => !files?.[0] || files[0].size / 1024 / 1024 <= 5 || 'Max 5MB'
                    })}
                  />
                </label>
                {errors.dishImage && <p className="form-error">{errors.dishImage.message}</p>}
              </div>

              {/* English Name */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNameEn')} <span className="required">*</span></label>
                <input
                  type="text"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Min 2 chars' } })}
                />
                {errors.name && <p className="form-error">{errors.name.message}</p>}
              </div>

              {/* Chinese Name */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNameZh')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 黄油鸡"
                  {...register('nameZh')}
                />
              </div>

              {/* Dish / Item Number — admin + receipt only, never shown in the mini program */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNumber')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 97A, 21B"
                  maxLength={20}
                  {...register('dishNumber')}
                />
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  {t('dishes.dishNumberHint')}
                </small>
              </div>

              {/* Variants toggle */}
              <div className="form-group" style={{ background: '#FFF8E7', border: '2px solid #E8D5A8', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label className="form-label mb-0" style={{ margin: 0 }}>
                    <i className="bi bi-rulers me-1" style={{ color: '#a05a2c' }}></i>
                    {t('dishes.multiSize')}
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={useVariants}
                      onChange={toggleUseVariants}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#6B5347' }}>
                      {useVariants ? t('dishes.enabled') : t('dishes.useOnePrice')}
                    </span>
                  </label>
                </div>
                <small style={{ color: '#8B4A24', fontSize: 12, lineHeight: 1.5, display: 'block' }}>
                  Enable for dishes with multiple sizes like <b>"2pcs / 4pcs"</b> or <b>"Small / Big"</b>.
                </small>
              </div>

              {!useVariants && (
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('dishes.price')} <span className="required">*</span></label>
                    <input
                      type="number" step="0.01" min="0.01"
                      className={`form-input ${errors.price ? 'error' : ''}`}
                      {...register('price', { required: !useVariants && 'Price is required', min: { value: 0.01, message: 'Must be > 0' } })}
                    />
                    {errors.price && <p className="form-error">{errors.price.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('dishes.discountPrice')}</label>
                    <input type="number" step="0.01" min="0" className="form-input" {...register('discountPrice')} />
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div>
              {/* Category English */}
              <div className="form-group">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">{t('dishes.categoryEn')} <span className="required">*</span></label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', padding: 0 }}
                    onClick={() => setShowNewCat(v => !v)}
                  >
                    {showNewCat ? t('dishes.useExisting') : t('dishes.newCategory')}
                  </button>
                </div>

                {showNewCat ? (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('dishes.typeNewCat')}
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                  />
                ) : (
                  <select
                    className={`form-select-input ${errors.category ? 'error' : ''}`}
                    {...register('category', { required: !showNewCat })}
                  >
                    <option value="">{t('dishes.selectCategory')}</option>
                    {categories.map(c => (
                      <option key={c.name} value={c.name}>
                        {c.nameZh ? `${c.name} / ${c.nameZh}` : c.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.category && !showNewCat && <p className="form-error">Category is required</p>}
              </div>

              {/* Category Chinese */}
              <div className="form-group">
                <label className="form-label">{t('dishes.categoryZh')}</label>
                {showNewCat ? (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. 主菜"
                    value={newCategoryZh}
                    onChange={e => setNewCategoryZh(e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('dishes.autoFilled')}
                    {...register('categoryZh')}
                  />
                )}
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">{t('dishes.description')} <span className="required">*</span></label>
                <textarea
                  rows="4"
                  className={`form-textarea ${errors.description ? 'error' : ''}`}
                  {...register('description', { required: 'Description is required', minLength: { value: 10, message: 'Min 10 chars' } })}
                ></textarea>
                {errors.description && <p className="form-error">{errors.description.message}</p>}
              </div>

              {/* Popular */}
              <div className="form-group form-check-row">
                <input type="checkbox" id="editIsPopular" className="form-check-input" {...register('isPopular')} />
                <label htmlFor="editIsPopular" className="form-check-label">
                  <i className="bi bi-star me-1"></i>{t('dishes.markPopular')}
                </label>
              </div>
            </div>
          </div>

          {/* Variants list */}
          <AnimatePresence>
            {useVariants && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <VariantEditor variants={variants} onUpdate={updateVariant} onRemove={removeVariant} onAdd={addVariant} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" onClick={() => navigate('/dishes')} className="btn-cancel-form" disabled={submitLoading}>
              <i className="bi bi-x-circle me-2"></i>{t('common.cancel')}
            </button>
            <button type="submit" className="btn-submit-form" disabled={submitLoading}>
              {submitLoading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</>
              ) : (
                <><i className="bi bi-save me-2"></i>{t('dishes.updateDish')}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
